<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview
        v-bind:title="'Chi tiết ngân sách tháng ' + formatPeriod(line.period)"
      >
        <template v-slot:toolbar>
          <b-button
            v-if="canEdit && checkPermission('BUDGET_UPDATE') && !isEditRoute"
            variant="primary"
            size="sm"
            class="mr-2"
            @click="enterEditMode"
          >
            <i class="fas fa-edit"></i> Chỉnh sửa
          </b-button>
          <b-button
            v-if="line.canApprove && !line.readOnlyApproval && checkPermission('BUDGET_UPDATE')"
            variant="success"
            size="sm"
            class="mr-2"
            @click="approveAction"
          >
            <i class="fas fa-check"></i> Duyệt
          </b-button>
          <b-button
            v-if="line.canApprove && !line.readOnlyApproval && checkPermission('BUDGET_UPDATE')"
            variant="danger"
            size="sm"
            class="mr-2"
            @click="showRejectModal"
          >
            <i class="fas fa-times"></i> Không duyệt
          </b-button>
          <b-button variant="secondary" size="sm" @click="showHistoryModal">
            <i class="flaticon2-time"></i>&nbsp; Lịch sử
          </b-button>
        </template>

        <template v-slot:preview>
          <div v-if="isEditRoute" class="mb-4">
            <b-row>
              <b-col cols="6">
                <b-form-group>
                  <template #label>
                    Số tiền phân bổ <span class="text-danger">*</span>
                  </template>
                  <BInputNumber
                    v-model="editForm.allocatedAmount"
                    placeholder="0"
                    size="sm"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group>
                  <template #label>
                    Lý do chỉnh sửa <span class="text-danger">*</span>
                  </template>
                  <b-form-textarea
                    v-model="editForm.description"
                    rows="3"
                    placeholder="Nhập lý do"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <div class="d-flex">
              <b-button
                variant="primary"
                :disabled="isSavingEdit"
                @click="submitEdit"
              >
                <i class="fas fa-save"></i>
                {{ isSavingEdit ? 'Đang lưu...' : 'Lưu' }}
              </b-button>
              <b-button
                variant="secondary"
                class="ml-2"
                :disabled="isSavingEdit"
                @click="exitEditMode"
              >
                Hủy
              </b-button>
            </div>
          </div>

          <b-row class="mb-4">
            <b-col cols="3">
              <small class="text-muted">Kỳ</small>
              <div>
                <strong>{{ formatPeriod(line.period) }}</strong>
              </div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Trạng thái</small>
              <div>
                <b-badge :variant="statusVariant(line.status)">
                  {{ statusName(line.status) }}
                </b-badge>
              </div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Phòng ban</small>
              <div>{{ departmentName || line.departmentId }}</div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Cửa hàng</small>
              <div>
                {{
                  storeName || (line.storeId ? line.storeId : 'Cấp phòng ban')
                }}
              </div>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="6">
              <small class="text-muted">Doanh nghiệp</small>
              <div>{{ line.companyName || line.companyId }}</div>
            </b-col>
            <b-col cols="6">
              <small class="text-muted">Khoản mục phí</small>
              <div>
                {{ line.costItem ? line.costItem.name : line.costItemId }}
              </div>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="4">
              <PurchaseRequestApprovalSummary
                :approvers="line.approvers || []"
              />
            </b-col>
          </b-row>

          <b-row class="mt-4">
            <b-col cols="4">
              <div class="bl-metric-card">
                <div class="text-muted bl-metric-label">Phân bổ</div>
                <div class="bl-metric-big">
                  {{ formatCompactMoney(line.allocatedAmount) }}
                </div>
                <div class="text-muted bl-metric-small">
                  {{ convertPrice(line.allocatedAmount) }} đ
                </div>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="bl-metric-card">
                <div class="text-muted bl-metric-label">Đã sử dụng</div>
                <div class="bl-metric-big">
                  {{ formatCompactMoney(line.usedAmount) }}
                </div>
                <div class="text-muted bl-metric-small">
                  {{ budgetUsedPercent }}% phân bổ
                </div>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="bl-metric-card bl-metric-card--remaining">
                <div class="text-muted bl-metric-label">Còn lại</div>
                <div
                  class="bl-metric-big"
                  :class="
                    line.remainingAmount < 0 ? 'text-danger' : 'text-success'
                  "
                >
                  {{ formatCompactMoney(line.remainingAmount) }}
                </div>
                <div class="text-muted bl-metric-small">
                  {{ convertPrice(line.remainingAmount) }} đ
                </div>
              </div>
            </b-col>
          </b-row>

          <div class="bl-progress-row mb-4">
            <div class="d-flex justify-content-between">
              <div class="text-muted">
                {{ formatCompactMoney(line.usedAmount) }} /
                {{ formatCompactMoney(line.allocatedAmount) }}
              </div>
              <div class="text-muted font-weight-bold">
                {{ budgetUsedPercent }}%
              </div>
            </div>
            <b-progress
              :value="budgetUsedPercent"
              :max="100"
              height="8px"
              :variant="budgetProgressVariant"
              class="mt-1"
            />
          </div>

          <div class="mt-3">
            <b-alert
              v-for="(a, idx) in line.budgetAlerts || []"
              :key="`${a.code || 'ALERT'}-${idx}`"
              show
              :variant="a.variant || 'warning'"
              class="mb-2"
            >
              <i class="fas fa-exclamation-triangle"></i>
              <div>{{ a.message }}</div>
            </b-alert>
          </div>

          <BudgetLineHistoryModal ref="historyModal" :histories="histories" />

          <b-button variant="secondary" @click="goBack" class="mt-3">
            <i class="fas fa-arrow-left"></i> Quay lại
          </b-button>

          <b-modal
            ref="rejectModal"
            title="Không duyệt ngân sách"
            @ok="confirmReject"
            ok-variant="danger"
            ok-title="Không duyệt"
            cancel-title="Đóng"
          >
            <b-form-group>
              <template #label>
                Lý do <span class="text-danger">*</span>
              </template>
              <b-form-textarea v-model="rejectForm.note" rows="3" required />
            </b-form-group>
          </b-modal>

          <!-- Modal duyệt ngân sách -->
          <b-modal
            ref="approveModal"
            title="Duyệt ngân sách"
            @ok="confirmApprove"
            ok-variant="success"
            ok-title="Duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú duyệt">
              <b-form-textarea
                v-model="approveForm.note"
                rows="3"
                placeholder="Nhập ghi chú (nếu có)"
              />
            </b-form-group>
          </b-modal>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
} from '@/utils/common';
import moment from 'moment';
import BudgetLineHistoryModal from './components/BudgetLineHistoryModal.vue';
import PurchaseRequestApprovalSummary from '../purchase-requests/components/PurchaseRequestApprovalSummary.vue';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import {
  BUDGET_STATUS,
  BUDGET_STATUS_BADGE_VARIANT,
  APPROVER_STATUS_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
} from '@/utils/enum';
import {
  BUDGET_STATUS_NAME,
  APPROVER_STATUS_NAME,
  HISTORY_ACTION_TYPE_CODE,
} from '@/utils/enum-name';

export default {
  components: {
    KTCodePreview,
    PurchaseRequestApprovalSummary,
    BudgetLineHistoryModal,
    BInputNumber,
  },
  data() {
    return {
      line: {},
      histories: [],
      departmentName: '',
      storeName: '',
      editForm: { id: null, allocatedAmount: 0, description: '' },
      isSavingEdit: false,
      rejectForm: { note: '' },
      approveForm: { note: '' },
      approverFields: [
        {
          key: 'step',
          label: 'Bước',
          thStyle: { width: '8%' },
          class: 'text-center',
        },
        { key: 'approverBy', label: 'Người duyệt', thStyle: { width: '20%' } },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { width: '15%' },
          class: 'text-center',
        },
        { key: 'note', label: 'Ghi chú' },
        {
          key: 'updatedAt',
          label: 'Thời gian',
          thStyle: { width: '15%' },
          class: 'text-center',
        },
      ],
      historyFields: [
        { key: 'createdAt', label: 'Thời gian', thStyle: { width: '15%' } },
        { key: 'actionLabel', label: 'Hành động', thStyle: { width: '15%' } },
        { key: 'description', label: 'Chi tiết' },
        {
          key: 'originalValueText',
          label: 'Giá trị cũ',
          thStyle: { width: '22%' },
        },
        {
          key: 'newValueText',
          label: 'Giá trị mới',
          thStyle: { width: '22%' },
        },
        {
          key: 'updatedBy',
          label: 'Người thực hiện',
          thStyle: { width: '10%' },
        },
      ],
    };
  },
  computed: {
    isEditRoute() {
      const v = this.$route?.query?.edit;
      return v === 1 || v === '1' || v === true || v === 'true';
    },
    canEdit() {
      return Number(this.line?.status) === Number(BUDGET_STATUS.DRAFT);
    },
    budgetUsedPercent() {
      const allocated = Number(this.line?.allocatedAmount);
      const used = Number(this.line?.usedAmount);
      if (!allocated || allocated <= 0) return 0;
      const raw = (used / allocated) * 100;
      if (!Number.isFinite(raw)) return 0;
      return Math.max(0, Math.min(100, Math.round(raw)));
    },
    budgetProgressVariant() {
      const remaining = Number(this.line?.remainingAmount);
      if (Number.isFinite(remaining) && remaining < 0) return 'danger';
      if (this.budgetUsedPercent >= 90) return 'warning';
      return 'success';
    },
  },
  created() {
    const id = this.$route.query.id;
    if (id) {
      this.fetchDetail(id);
      this.fetchHistory(id);
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Ngân sách', route: '/budget/budget-lines' },
      { title: 'Chi tiết' },
    ]);
  },
  methods: {
    checkPermission,
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    enterEditMode() {
      const id = this.$route.query.id;
      if (!id) return;
      this.$router
        .push({ name: 'detail-budget-line', query: { id, edit: 1 } })
        .catch(() => {});
    },
    exitEditMode() {
      const id = this.$route.query.id;
      this.$router
        .replace({ name: 'detail-budget-line', query: { id } })
        .catch(() => {});
    },
    initEditForm() {
      this.editForm = {
        id: this.line?.id || null,
        allocatedAmount: Number(this.line?.allocatedAmount) || 0,
        description: '',
      };
    },
    submitEdit() {
      if (!this.editForm?.id) return;
      if (!this.editForm.description || !this.editForm.description.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do chỉnh sửa!');
        return;
      }
      this.isSavingEdit = true;
      ApiService.put('v2/budget-lines', this.editForm)
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchDetail(this.editForm.id);
          this.fetchHistory(this.editForm.id);
          this.exitEditMode();
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isSavingEdit = false;
        });
    },
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    formatCompactMoney(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return '--';
      const abs = Math.abs(n);
      if (abs >= 1e9) {
        const v = n / 1e9;
        return `${this.trimCompactDecimals(v)}B`;
      }
      if (abs >= 1e6) {
        const v = n / 1e6;
        return `${this.trimCompactDecimals(v)}M`;
      }
      if (abs >= 1e3) {
        const v = n / 1e3;
        return `${this.trimCompactDecimals(v)}K`;
      }
      return String(Math.round(n));
    },
    trimCompactDecimals(v) {
      const rounded = Math.round(v * 10) / 10;
      return Number.isInteger(rounded) ? String(rounded) : String(rounded);
    },
    historyActionLabel(action) {
      return HISTORY_ACTION_TYPE_CODE[action] || String(action || '');
    },
    formatJson(val) {
      if (val === null || val === undefined) return '--';
      if (typeof val === 'string') return val || '--';
      try {
        const str = JSON.stringify(val);
        return str.length > 2000 ? `${str.slice(0, 2000)}...` : str;
      } catch (e) {
        return String(val);
      }
    },
    statusVariant(val) {
      return BUDGET_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    statusName(val) {
      return BUDGET_STATUS_NAME[val] || '';
    },
    formatNextApprovalStep(row) {
      if (!row || row.status !== BUDGET_STATUS.DRAFT) return '--';
      const step = row.nextApprovalStep;
      if (step && step.level) {
        return `${step.statusName || ''} bước ${step.level}`.trim();
      }
      return '--';
    },
    approverStatusVariant(val) {
      return APPROVER_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    approverStatusName(val) {
      return APPROVER_STATUS_NAME[val] || val;
    },
    fetchDetail(id) {
      ApiService.get(`v2/budget-lines/${id}`)
        .then(({ data }) => {
          this.line = data.data || {};
          if (this.isEditRoute) {
            this.initEditForm();
          }
          // optional name resolving (best-effort)
          ApiService.query('departments/all').then(({ data: depRes }) => {
            const rows = depRes.data || [];
            const found = rows.find(
              (d) => Number(d.id) === Number(this.line.departmentId),
            );
            this.departmentName = found ? found.name : '';
          });
          ApiService.query('stores/getStores').then(({ data: storeRes }) => {
            const rows = storeRes.data || [];
            const found = rows.find(
              (s) => Number(s.id) === Number(this.line.storeId),
            );
            this.storeName = found
              ? found.shortName
                ? `${found.name} (${found.shortName})`
                : found.name
              : '';
          });
          console.log(this.line);
        })
        .catch((err) => handleError(err));
    },
    fetchHistory(id) {
      ApiService.get(`v2/budget-lines/${id}/history`)
        .then(({ data }) => {
          this.histories = (data.data || []).map((h) => ({
            ...h,
            actionLabel: this.historyActionLabel(h.action),
            originalValueText: this.formatJson(h.originalValue),
            newValueText: this.formatJson(h.newValue),
          }));
        })
        .catch((err) => handleError(err));
    },
    showHistoryModal() {
      this.$refs.historyModal?.open();
    },
    approveAction() {
      this.approveForm.note = '';
      this.$refs.approveModal.show();
    },
    /** Giống PR: PATCH change-status/:id + WORKFLOW_APPROVAL_STEP_STATUS */
    patchBudgetLineChangeStatus(status, note) {
      return ApiService.patch(`v2/budget-lines/change-status/${this.line.id}`, {
        status,
        note: note != null ? String(note) : '',
      });
    },
    confirmApprove(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      this.patchBudgetLineChangeStatus(
        WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        this.approveForm.note || '',
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchDetail(this.line.id);
          this.fetchHistory(this.line.id);
          this.$refs.approveModal.hide();
        })
        .catch((err) => handleError(err));
    },
    showRejectModal() {
      this.rejectForm.note = '';
      this.$refs.rejectModal.show();
    },
    confirmReject(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      if (!this.rejectForm.note.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do không duyệt!');
        return;
      }
      this.patchBudgetLineChangeStatus(
        WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
        this.rejectForm.note,
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchDetail(this.line.id);
          this.fetchHistory(this.line.id);
          this.$refs.rejectModal.hide();
        })
        .catch((err) => handleError(err));
    },
    goBack() {
      const from = this.$route?.query?.from;
      if (from) {
        this.$router.push(String(from)).catch(() => {});
        return;
      }
      this.$router.push({ name: 'list-budget-lines' }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.bl-metric-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
  min-height: 110px;
}

.bl-metric-card--remaining {
  background: #e9fbf3;
}

.bl-metric-label {
  font-size: 0.875rem;
}

.bl-metric-big {
  font-size: 1.6rem;
  font-weight: 900;
  line-height: 1.2;
  margin-top: 6px;
}

.bl-metric-small {
  font-size: 0.875rem;
  margin-top: 6px;
}

.bl-progress-row {
  background: #ffffff;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.03);
}
</style>

